import styled from 'styled-components'
import { Link, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { useEffect, useCallback, Fragment, useMemo } from 'react'
import { useMap } from 'react-map-gl'
import {
	getChargerData,
	getPostsData,
	getZapsData,
	getRecentChargesData,
	authenticateSession,
} from '../utility/api'
import {
	useAuthState,
	useChargerState,
	useLocalizationState,
	useMapState,
} from '../state/context'
import {
	receiveActiveZap,
	receiveAwards,
	receiveData,
	receiveDataError,
	receivePopularTimes,
	receivePosts,
	receivePostsError,
	receiveZaps,
	receiveZapsError,
	fetchingRecentCharges,
	receiveRecentCharges,
	receiveRecentError,
	resetActiveChargerState,
	receiveSavedView,
	fetchingData,
	fetchingPosts,
	fetchingZaps,
	updateLoadMap,
	receiveCountryCode,
	receiveCurrencyCode,
	updateAuthenticating,
	updateShouldAuthenticate,
} from '../state/actions'
import { returnTimeAgo } from '../utility/helpers'
import { useCurrentZap } from '../helpers/hooks'
import ZapsCarousel from '../components/chargerScreen/ZapsCarousel'
import ChargerInfo from '../components/chargerScreen/ChargerInfo'
import Pricing from '../components/chargerScreen/Pricing'
import PopularSection from '../components/chargerScreen/PopularSection'
import Awards from '../components/chargerScreen/Awards'
import PostsAndProblems from '../components/chargerScreen/PostsAndProblems'
import Footer from '../components/chargerScreen/Footer'
import RecentCharges from '../components/chargerScreen/RecentCharges'
import Skeleton from '../components/shared/Skeleton'
import ZapModal from '../components/chargerScreen/ZapModal'
import RetryButton from '../components/shared/RetryButton'
import Text from '../components/shared/Text'
import Button from '../components/shared/Button'
import {
	BackArrow,
	CancelIcon,
	FastCharge,
	GhostError,
	RegularCharge,
	StallIcon,
	fast_charger_pin,
	regular_charger_pin,
} from '../utility/icons'
import styles from '../utility/styles'

const FALLBACK_TITLE = 'TezLab | Charger Map'

const Wrapper = styled.div`
	position: absolute;
	top: 0;
	left: ${styles.spacing.navBar};
	bottom: 0;
	width: ${styles.spacing.chargerScreenSide};
	background-color: ${styles.colors.background};
	border-left: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.08));
	overflow: ${({ disableScroll }) => (disableScroll ? 'hidden' : 'scroll')};
	@media only screen and ${styles.breakpoints.desktop} {
		left: 0;
		right: 0;
		top: 64px;
		width: 100%;
		z-index: 100; // hide mapbox attribution logo
	}
	@media only screen and (max-width: 450px), screen and (max-height: 450px) {
		top: 64px;
		z-index: 1001;
	}
`

const Section = styled.div`
	padding: 0 32px 32px 32px;
	@media only screen and ${styles.breakpoints.desktop} {
		padding-left: calc(env(safe-area-inset-left) + 12px);
		padding-right: calc(env(safe-area-inset-right) + 12px);
	}
`

const OverflowSection = styled.div`
	padding: 0 0 32px 32px;
	@media only screen and ${styles.breakpoints.desktop} {
		padding-left: calc(env(safe-area-inset-left) + 12px);
	}
`

const StyledLink = styled(Link)`
	text-decoration: none;
`

const Row = styled.div`
	display: flex;
	align-items: center;
`

const ErrorContainer = styled.div`
	flex: 1;
	height: calc(100% - 72px);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`

const ErrorTextContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-top: 120px;
`

const Mask = styled.div`
	position: absolute;
	background-color: ${styles.colors.background};
	top: 0;
	right: 0;
	left: ${styles.spacing.chargerScreenSide};
	height: 100vh;
	background-image: url('${({ src }) => src}');
	background-size: cover;
	background-repeat: no-repeat;
	background-position: center;
	display: flex;
	justify-content: center;
	align-items: center;
`

const Pin = styled.img`
	height: 95px;
	margin-top: -47.5px;
`

export default function ChargerScreen() {
	const { id } = useParams()

	const [
		{
			data,
			error,
			isFetchingData,
			popularTimes,
			// popularTimesError,
			// isFetchingPopularTimes,
			awards,
			// awardsError,
			// isFetchingAwards,
			// ratings,
			// isFetchingRatings,
			// ratingsError,
			posts,
			postsError,
			isFetchingPosts,
			problems,
			// problemsError,
			// isFetchingProblems,
			zaps,
			zapsError,
			isFetchingZaps,
			isFetchingRecent,
			recentCharges,
			recentChargesError,
		},
		dispatch,
	] = useChargerState()
	const [, mapDispatch] = useMapState()
	const [, localDispatch] = useLocalizationState()
	const [{ shouldAuthenticate }, authDispatch] = useAuthState()
	const currentZap = useCurrentZap()
	const { chargerMap } = useMap('chargerMap')

	const onZapClick = id => dispatch(receiveActiveZap(id))

	const closeZap = () => dispatch(receiveActiveZap(null))

	const pin = useMemo(
		() => (data?.power_kw > 150 ? fast_charger_pin : regular_charger_pin),
		[data],
	)

	// intercept back handler and reset charger state, restore saved zoom view and nullify the saved view in state
	const onClick = useCallback(() => {
		dispatch(resetActiveChargerState())
	}, [dispatch])

	// load map and zoom to this location
	const onMapClick = useCallback(() => {
		mapDispatch(
			receiveSavedView({
				center: [data?.location?.lng, data?.location?.lat],
				zoom: 15,
			}),
		)
		mapDispatch(updateLoadMap(true))
	}, [mapDispatch, data])

	// retry functions

	const onRetry = useCallback(async () => {
		try {
			dispatch(receiveDataError(''))
			dispatch(fetchingData(true))
			const {
				data: d,
				awards,
				popular_times,
				error,
			} = await getChargerData(id)
			if (error) throw error
			dispatch(receiveData(d))
			dispatch(receivePopularTimes(popular_times))
			dispatch(receiveAwards(awards))
			dispatch(fetchingData(false))
		} catch (error) {
			dispatch(receiveDataError(error))
			dispatch(fetchingData(false))
		}
	}, [id, dispatch])

	const onRetryPosts = useCallback(async () => {
		try {
			dispatch(receivePostsError(''))
			dispatch(fetchingPosts(true))
			const { data: d, error } = await getPostsData(data.posts)
			if (error) throw error
			dispatch(receivePosts(d))
			dispatch(fetchingPosts(false))
		} catch (error) {
			dispatch(receivePostsError(error))
			dispatch(fetchingPosts(false))
		}
	}, [data, dispatch])

	const onRetryRecent = useCallback(async () => {
		try {
			dispatch(receiveRecentError(''))
			fetchingRecentCharges(true)
			const { data: d, error } = await getRecentChargesData(data.recent)
			if (error) throw error
			dispatch(receiveRecentCharges(d))
			fetchingRecentCharges(false)
		} catch (error) {
			fetchingRecentCharges(false)
			dispatch(receiveRecentError(error))
		}
	}, [data, dispatch])

	const onRetryZaps = useCallback(async () => {
		try {
			dispatch(receiveZapsError(''))
			dispatch(fetchingZaps(true))
			const { data: d, error } = await getZapsData(data.zaps)
			if (error) throw error
			dispatch(receiveZaps(d))
			dispatch(fetchingZaps(false))
		} catch (error) {
			dispatch(receiveZapsError(error))
			dispatch(fetchingZaps(false))
		}
	}, [data, dispatch])

	// effects used to fetch the data

	useEffect(() => {
		async function fetchData() {
			// Server-injected payload (see ChargerMapPage): render without the session
			// handshake so stateless crawlers never hit the fetch-error screen. Consumed
			// once — SPA navigations to other chargers fall through to the normal fetch.
			const bootstrap = window.__CHARGER_BOOTSTRAP__
			if (bootstrap && bootstrap.id === id) {
				delete window.__CHARGER_BOOTSTRAP__
				const { awards, congestion, ...d } = bootstrap
				// countryCode/currencyCode come from response headers, so bootstrapped
				// loads deliberately skip those dispatches (nothing consumes them today).
				dispatch(receiveDataError(''))
				dispatch(receiveData(d))
				dispatch(receivePopularTimes(congestion))
				dispatch(receiveAwards(awards))
				dispatch(fetchingData(false))
				return
			}
			try {
				if (shouldAuthenticate) {
					authDispatch(updateAuthenticating(true))
					await authenticateSession()
					authDispatch(updateAuthenticating(true))
					authDispatch(updateShouldAuthenticate(false))
				}
				dispatch(receiveDataError(''))
				dispatch(fetchingData(true))
				const {
					data: d,
					awards,
					popular_times,
					error,
					countryCode,
					currencyCode,
				} = await getChargerData(id)
				if (error) throw error
				localDispatch(receiveCountryCode(countryCode))
				localDispatch(receiveCurrencyCode(currencyCode))
				dispatch(receiveData(d))
				dispatch(receivePopularTimes(popular_times))
				dispatch(receiveAwards(awards))
				dispatch(fetchingData(false))
			} catch (error) {
				dispatch(receiveDataError(error))
				dispatch(fetchingData(false))
			}
		}
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, dispatch, localDispatch, authDispatch])

	useEffect(() => {
		if (!data) return
		async function fetchRecentCharges() {
			try {
				dispatch(receiveRecentError(''))
				fetchingRecentCharges(true)
				const { data: d, error } = await getRecentChargesData(
					data.recent,
				)
				if (error) throw error
				dispatch(receiveRecentCharges(d))
				fetchingRecentCharges(false)
			} catch (error) {
				fetchingRecentCharges(false)
				dispatch(receiveRecentError(error))
			}
		}
		fetchRecentCharges()
	}, [data, dispatch])

	useEffect(() => {
		if (!data) return
		async function fetchPosts() {
			try {
				dispatch(receivePostsError(''))
				dispatch(fetchingPosts(true))
				const { data: d, error } = await getPostsData(data.posts)
				if (error) throw error
				dispatch(receivePosts(d))
				dispatch(fetchingPosts(false))
			} catch (error) {
				dispatch(receivePostsError(error))
				dispatch(fetchingPosts(false))
			}
		}
		fetchPosts()
	}, [data, dispatch])

	useEffect(() => {
		if (!data) return
		async function fetchZaps() {
			try {
				dispatch(receiveZapsError(''))
				dispatch(fetchingZaps(true))
				const { data: zaps, error } = await getZapsData(data.zaps)
				if (error) throw error
				dispatch(receiveZaps(zaps))
				dispatch(fetchingZaps(false))
			} catch (error) {
				dispatch(receiveZapsError(error))
				dispatch(fetchingZaps(false))
			}
		}
		fetchZaps()
	}, [data, dispatch])

	if (error)
		return (
			<Wrapper style={{ overflow: 'hidden' }}>
				<Section
					style={{ display: 'flex', justifyContent: 'flex-end' }}
				>
					<StyledLink to='/' {...{ onClick }}>
						<Button
							variant='secondary'
							style={{ marginTop: '32px', padding: '16px' }}
						>
							<CancelIcon />
						</Button>
					</StyledLink>
				</Section>
				<ErrorContainer>
					<GhostError />
					<ErrorTextContainer>
						<Text.Heading
							type='h5'
							style={{
								textAlign: 'center',
								marginBottom: '24px',
							}}
						>
							{error}
						</Text.Heading>
						<RetryButton onClick={onRetry} variant='secondary' />
					</ErrorTextContainer>
				</ErrorContainer>
			</Wrapper>
		)

	return (
		<Fragment>
			<Helmet>
				<title>{data?.meta?.page_title || FALLBACK_TITLE}</title>
				{data?.meta?.meta_fields?.map(({ name, content }) => (
					<meta key={name} {...{ name, content }} />
				))}
				{/* JSON-LD is server-injected by ChargerMapPage; emitting it here
				    again gave Google two aggregateRating nodes for the same @id. */}
			</Helmet>
			{!Boolean(chargerMap) && (
				<Mask src={data?.tiles?.map} onClick={onMapClick}>
					{Boolean(data?.tiles?.map) && (
						<Pin src={pin} alt='charger pin' />
					)}
				</Mask>
			)}
			<Wrapper disableScroll={!!currentZap}>
				<ZapModal {...{ currentZap }} close={closeZap} />
				<Section
					style={{ display: 'flex', justifyContent: 'flex-end' }}
				>
					<StyledLink to='/' {...{ onClick }}>
						<Button
							variant='secondary'
							style={{
								marginTop: '32px',
								padding: '16px',
								borderRadius: '8px',
							}}
						>
							<CancelIcon />
						</Button>
					</StyledLink>
				</Section>
				<Section>
					<Text.Heading type='h2' variant='bold'>
						{data?.name}
					</Text.Heading>
					<Text.Body color={styles.colors.lightGray}>
						Tesla Supercharger
					</Text.Body>
				</Section>
				<Skeleton
					isLoading={isFetchingData}
					height='360px'
					style={{ margin: '24px' }}
				>
					<Section>
						<Row>
							<Row style={{ flex: 1 }}>
								{data?.power_kw > 150 ? (
									<FastCharge />
								) : (
									<RegularCharge />
								)}
								<Text.Heading
									type='h5'
									variant='bold'
									style={{ marginLeft: '4px' }}
								>
									{data?.power_kw}kW
								</Text.Heading>
							</Row>
							{data?.stalls?.total && (
								<Row style={{ flex: 1 }}>
									<StallIcon />
									{!!data?.stalls?.available ? (
										<Text.Heading
											type='h5'
											variant='bold'
											style={{ marginLeft: '4px' }}
										>
											{data?.stalls?.available}/
											{data?.stalls?.total} Open
										</Text.Heading>
									) : (
										<Text.Heading
											type='h5'
											variant='bold'
											style={{ marginLeft: '4px' }}
										>
											{data?.stalls?.total} Stalls
										</Text.Heading>
									)}
								</Row>
							)}
						</Row>
						{(Number(data?.stalls?.as_of) > 0 || (!!data?.stalls?.as_of && isNaN(Number(data?.stalls?.as_of)) && !isNaN(Date.parse(data?.stalls?.as_of)))) && (
							<Text.Caption color={styles.colors.lightGray}>
								Last Updated {returnTimeAgo(data?.stalls?.as_of)}
							</Text.Caption>
						)}
					</Section>
					<Skeleton
						isLoading={isFetchingZaps}
						height='250px'
						style={{ margin: '24px' }}
					>
						<OverflowSection
							style={{ display: 'flex', paddingRight: 0 }}
						>
							<ZapsCarousel
								{...{ zapsError, onZapClick }}
								zapsArray={zaps}
								retry={onRetryZaps}
								isFastCharger={data?.power_kw > 150}
								imageSrc={data?.tiles?.sat}
							/>
						</OverflowSection>
					</Skeleton>
					<Section>
						<ChargerInfo
							ratings={data?.rating_val}
							amenities={data?.features?.amenities}
							commentsNum={data?.social_meta?.post_count}
							mayor={
								awards?.find(el => el.award === 'mayor')?.name
							}
							chargerInCharger={
								awards?.find(
									el => el.award === 'charger_in_charge',
								)?.name
							}
							location={[
								parseFloat(data?.location?.lat),
								parseFloat(data?.location?.lng),
							].filter(num => !Number.isNaN(num))}
						/>
					</Section>
					{!!data?.ai_summary && (
						<Section>
							<Row style={{ marginBottom: '12px' }}>
								<Text.Heading
									type='h5'
									variant='bold'
									style={{ marginRight: '8px' }}
								>
									TezLab AI Summary
								</Text.Heading>
								<svg
									width='24'
									height='25'
									viewBox='0 0 24 25'
									fill='none'
									xmlns='http://www.w3.org/2000/svg'
								>
									<path
										d='M16.6201 13.959C16.8939 13.6953 17.3287 13.6766 17.625 13.9277C17.9211 14.1788 17.9739 14.6105 17.7588 14.9238L17.7119 14.9853L17.3457 15.4082C16.9719 15.8311 16.5774 16.2517 16.1641 16.665C13.7992 19.0298 11.2173 20.7803 8.92968 21.6826C7.78688 22.1333 6.68159 22.3861 5.6953 22.373C4.70442 22.3598 3.78193 22.0748 3.1035 21.3965C2.31292 20.6057 2.05922 19.4892 2.14257 18.3096C2.21561 17.2763 2.54776 16.1262 3.08495 14.9365L3.32811 14.4258L3.3662 14.3584C3.5685 14.0366 3.98505 13.9136 4.333 14.0859C4.68068 14.2582 4.83517 14.6638 4.70214 15.0195L4.67186 15.0908L4.45018 15.5596C3.96462 16.6361 3.69574 17.6079 3.63866 18.415C3.57338 19.3387 3.78756 19.9593 4.16405 20.3359C4.48896 20.6607 4.99094 20.8634 5.71581 20.873C6.44551 20.8827 7.34953 20.6936 8.37792 20.2881C10.4329 19.4777 12.8484 17.8595 15.1035 15.6045C15.4951 15.2129 15.8685 14.8155 16.2217 14.416L16.5683 14.0146L16.6201 13.959ZM13.2627 6.74413C13.5278 6.42638 14.0003 6.38369 14.3183 6.64843C14.9408 7.16735 15.5603 7.73119 16.165 8.33593C18.5297 10.7008 20.2803 13.2827 21.1826 15.5703C21.6333 16.7131 21.8852 17.8184 21.8721 18.8047C21.8588 19.7955 21.5747 20.7181 20.8965 21.3965C20.0798 22.213 18.9162 22.4572 17.6924 22.3486C16.4654 22.2397 15.0788 21.7721 13.6553 21.0351C13.2876 20.8446 13.1436 20.3922 13.334 20.0244C13.5244 19.6568 13.977 19.513 14.3447 19.7031C15.6622 20.3852 16.856 20.7684 17.8252 20.8545C18.7972 20.9407 19.4462 20.7243 19.8349 20.3359C20.1598 20.011 20.3634 19.5092 20.373 18.7842C20.3827 18.0545 20.1935 17.1504 19.7881 16.1221C18.9776 14.067 17.3586 11.6516 15.1035 9.39647C14.5304 8.82337 13.9456 8.28941 13.3584 7.79979C13.0406 7.53453 12.9976 7.06217 13.2627 6.74413ZM3.1035 3.6035C3.89426 2.81295 5.01082 2.55822 6.19042 2.64159C7.22373 2.71463 8.37381 3.04775 9.56347 3.58495L10.0752 3.82714L10.1416 3.86522C10.4635 4.06743 10.5863 4.48502 10.4141 4.833C10.2416 5.1807 9.83528 5.33642 9.47948 5.20311L9.40917 5.17284L8.94139 4.95116C7.86437 4.46524 6.89239 4.1948 6.08495 4.13768C5.16129 4.07239 4.5407 4.2876 4.16405 4.66405C3.83939 4.98884 3.6367 5.49038 3.62694 6.21483C3.61725 6.94473 3.80716 7.85012 4.21288 8.87889C5.0234 10.9338 6.64154 13.3485 8.89647 15.6035C9.28785 15.9949 9.68473 16.3678 10.084 16.7207L10.4853 17.0674L10.541 17.1211C10.8042 17.3949 10.823 17.8289 10.5723 18.125C10.3211 18.4211 9.88942 18.4731 9.57616 18.2578L9.51464 18.2119L9.09179 17.8457C8.66905 17.472 8.24917 17.0773 7.83593 16.6641C5.47087 14.2989 3.71969 11.7166 2.81737 9.4287C2.36683 8.28619 2.11395 7.18237 2.12694 6.19628C2.1401 5.20523 2.42511 4.28203 3.1035 3.6035ZM11.6133 9.96972C11.718 9.57241 12.2819 9.5724 12.3867 9.96972L12.6103 10.8203C12.7479 11.3432 13.1568 11.752 13.6797 11.8896L14.5303 12.1133C14.9275 12.2181 14.9275 12.7819 14.5303 12.8867L13.6797 13.1103C13.1568 13.2479 12.7479 13.6568 12.6103 14.1797L12.3867 15.0303C12.2819 15.4275 11.7181 15.4275 11.6133 15.0303L11.3896 14.1797C11.252 13.6568 10.8432 13.2479 10.3203 13.1103L9.46972 12.8867C9.07241 12.7819 9.07241 12.218 9.46972 12.1133L10.3203 11.8896C10.8432 11.752 11.252 11.3432 11.3896 10.8203L11.6133 9.96972ZM18.3037 2.62694C19.2947 2.6401 20.218 2.92511 20.8965 3.6035C21.687 4.39423 21.9417 5.51084 21.8584 6.69042C21.7749 7.87125 21.3509 9.20432 20.6719 10.5752C20.4879 10.9459 20.038 11.0986 19.667 10.915C19.2959 10.7312 19.1434 10.2803 19.3271 9.90917C19.9531 8.64553 20.297 7.50768 20.3623 6.58495C20.4276 5.66125 20.2124 5.04069 19.8359 4.66405C19.5111 4.33939 19.0096 4.1367 18.2851 4.12694C17.5552 4.11725 16.6499 4.30716 15.6211 4.71288C13.5661 5.52338 11.1515 7.14156 8.89647 9.39647C8.42617 9.86679 7.98189 10.3452 7.56737 10.8262C7.29694 11.1395 6.82435 11.1744 6.51073 10.9043C6.1971 10.6339 6.16142 10.1604 6.43163 9.84667C6.87046 9.33754 7.33968 8.83218 7.83593 8.33593C10.201 5.97091 12.7834 4.21968 15.0713 3.31737C16.2138 2.86684 17.3176 2.61395 18.3037 2.62694Z'
										fill='white'
									/>
								</svg>
							</Row>
							<Text.Body color={styles.colors.lightGray}>
								{data?.ai_summary}
							</Text.Body>
						</Section>
					)}
				</Skeleton>
				<Skeleton
					isLoading={isFetchingData}
					height='48px'
					style={{ margin: '24px' }}
				>
					<Section>
						<Pricing
							currency_code={data?.pricing?.currency_code}
							currency_symbol_foreign={
								data?.pricing?.currency_symbol_foreign
							}
							currency_symbol_local={
								data?.pricing?.currency_symbol_local
							}
							idle_fees={data?.pricing?.idle_fees}
							price_list={data?.pricing?.price_list}
							time_of_use_rates={data?.pricing?.time_of_use_rates}
							type={data?.pricing?.type}
							non_tesla_prices={data?.pricing?.non_tesla_prices}
						/>
					</Section>
				</Skeleton>
				<Skeleton
					isLoading={isFetchingData || isFetchingRecent}
					height='100px'
					style={{ margin: '24px' }}
				>
					<OverflowSection>
						<RecentCharges
							error={recentChargesError}
							retry={onRetryRecent}
							charges={recentCharges}
							power={data?.power_kw}
						/>
					</OverflowSection>
				</Skeleton>
				<Skeleton
					isLoading={isFetchingData}
					height='400px'
					style={{ margin: '24px' }}
				>
					<Section>
						<PopularSection
							popularTimes={popularTimes ?? []}
							ratings={data?.reviews ?? []}
						/>
					</Section>
				</Skeleton>
				<Skeleton
					isLoading={isFetchingData}
					height='150px'
					style={{ margin: '24px' }}
				>
					<Section>
						<Awards {...{ awards }} />
					</Section>
				</Skeleton>
				<Skeleton
					isLoading={isFetchingData || isFetchingPosts}
					height='300px'
					style={{ margin: '24px' }}
				>
					<Section>
						<PostsAndProblems
							retry={onRetryPosts}
							{...{ posts, problems, postsError }}
						/>
					</Section>
				</Skeleton>
				<Section>
					<Footer />
				</Section>
			</Wrapper>
		</Fragment>
	)
}
